import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useUser } from '../../contexts/UserContext';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Icon,
  Flex,
  useColorModeValue,
  Progress,
  Avatar,
  Tooltip,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Grid,
  IconButton,
} from '@chakra-ui/react';
import { 
  FaGlobe, 
  FaChartLine, 
  FaArrowUp,
  FaArrowDown,
  FaExternalLinkAlt,
  FaStar,
  FaTrophy,
  FaMedal,
  FaAward,
  FaSearch,
  FaChartBar,
  FaChartPie,
  FaLightbulb,
  FaRocket,
  FaCrown,
} from 'react-icons/fa';
import { SectionLabel } from './DashboardWrapper';
import { 
  BarChart, 
  Bar, 
  PieChart,
  Pie,
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  ReferenceLine,
} from 'recharts';
import BusinessReviewModal from '../BusinessReviewModal';
import { getBusinessImages } from '../../services/imageService';
import { getYearData } from '../../services/yearService';
import { logger } from '../../utils/logger';
import { createFavoriteStarPieLabelRenderer } from '../FavoriteStarIndicator';
import {
  getTrafficValue,
} from '../../utils/competitiveIntelHelpers';

// Modern color palette
const COLORS = {
  primary: '#3B82F6',
  secondary: '#8B5CF6',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  info: '#06B6D4',
  chart: [
    '#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444',
    '#06B6D4', '#EC4899', '#14B8A6', '#6366F1', '#F97316',
  ],
  gradient: {
    blue: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    green: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    orange: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    purple: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  }
};

const TrafficDashboard = ({ reviewsData = [], demoMode, demoData }) => {
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isBusinessModalOpen, setIsBusinessModalOpen] = useState(false);
  const [businessImages, setBusinessImages] = useState({});
  
  // Get favorite URIs from user context
  const { favoriteUris } = useUser();

  // Create a map from shortened business names to their maps_uri for favorites check
  const businessNameToUriMap = useMemo(() => {
    const map = {};
    if (reviewsData) {
      reviewsData.forEach(business => {
        const shortName = business.name?.split(' ').slice(0, 2).join(' ');
        if (shortName && business.maps_uri) {
          map[shortName] = business.maps_uri;
        }
      });
    }
    return map;
  }, [reviewsData]);

  // Custom label renderer for bar charts that shows spinning stars for favorited businesses
  const FavoriteStarBarLabel = useCallback((props) => {
    const { x, y, width, payload } = props;
    const datumName = payload?.name ?? props?.name;
    
    // Check if this business is favorited
    const mapsUri = payload?.maps_uri || payload?.mapsUri || businessNameToUriMap[datumName];
    const isFavorited = mapsUri && favoriteUris?.includes(mapsUri);
    
    if (!isFavorited) return null;
    
    const starSize = 18;
    const starX = x + (width / 2);
    // Keep the star within the visible SVG area (top bars can otherwise clip it)
    const starY = Math.max(y - starSize - 4, 2);
    
    return (
      <g transform={`translate(${starX - starSize/2}, ${starY})`}>
        <g>
          <animateTransform
            attributeName="transform"
            attributeType="XML"
            type="rotate"
            from={`0 ${starSize/2} ${starSize/2}`}
            to={`360 ${starSize/2} ${starSize/2}`}
            dur="3s"
            repeatCount="indefinite"
          />
          <path
            d={`M${starSize/2 + 1},${2} L${starSize*0.63 + 1},${starSize*0.38 + 2} L${starSize + 1},${starSize*0.38 + 2} L${starSize*0.69 + 1},${starSize*0.62 + 2} L${starSize*0.81 + 1},${starSize + 2} L${starSize/2 + 1},${starSize*0.75 + 2} L${starSize*0.19 + 1},${starSize + 2} L${starSize*0.31 + 1},${starSize*0.62 + 2} L${1},${starSize*0.38 + 2} L${starSize*0.37 + 1},${starSize*0.38 + 2} Z`}
            fill="#8B7355"
            fillOpacity="0.3"
          />
          <path
            d={`M${starSize/2},0 L${starSize*0.63},${starSize*0.38} L${starSize},${starSize*0.38} L${starSize*0.69},${starSize*0.62} L${starSize*0.81},${starSize} L${starSize/2},${starSize*0.75} L${starSize*0.19},${starSize} L${starSize*0.31},${starSize*0.62} L0,${starSize*0.38} L${starSize*0.37},${starSize*0.38} Z`}
            fill="#D4AF37"
            stroke="#B8860B"
            strokeWidth="1"
          />
          <path
            d={`M${starSize/2},0 L${starSize*0.63},${starSize*0.38} L${starSize},${starSize*0.38} L${starSize*0.69},${starSize*0.62} L${starSize*0.81},${starSize} L${starSize/2},${starSize*0.75} L${starSize*0.19},${starSize} L${starSize*0.31},${starSize*0.62} L0,${starSize*0.38} L${starSize*0.37},${starSize*0.38} Z`}
            fill="#FFD700"
            fillOpacity="0.7"
          />
          <ellipse
            cx={starSize/2}
            cy={starSize*0.3}
            rx={starSize*0.18}
            ry={starSize*0.14}
            fill="#FFFACD"
            fillOpacity="0.8"
          />
        </g>
      </g>
    );
  }, [businessNameToUriMap, favoriteUris]);

  // Custom label renderer for pie charts that shows spinning stars for favorited businesses
  const FavoriteStarPieLabel = useMemo(() => {
    return createFavoriteStarPieLabelRenderer({
      businessNameToUriMap,
      favoriteUris
    });
  }, [businessNameToUriMap, favoriteUris]);

  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('visitors');
  const [sortDirection, setSortDirection] = useState('desc');

  // Year data state (fetched internally like other dashboards)
  const [yearData, setYearData] = useState([]);
  const [combinedData, setCombinedData] = useState([]);
  
  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.400');
  const mutedColor = useColorModeValue('gray.500', 'gray.500');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const glassBg = useColorModeValue('rgba(255,255,255,0.8)', 'rgba(26,32,44,0.8)');

  // Fetch business images
  useEffect(() => {
    if (demoMode) return;
    let mounted = true;
    const fetchImages = async () => {
      try {
        const images = await getBusinessImages();
        if (mounted && images) {
          const imageMap = {};
          images.forEach(img => {
            if (img.maps_uri && img.high_res_image_url_fetched) {
              imageMap[img.maps_uri] = img.high_res_image_url_fetched;
            }
          });
          setBusinessImages(imageMap);
        }
      } catch (error) {
        logger.error('Error fetching business images:', error);
      }
    };
    fetchImages();
    return () => { mounted = false; };
  }, [demoMode]);

  // Fetch year data and combine with reviews data
  useEffect(() => {
    if (demoMode && demoData) {
      setYearData(demoData.yearData || []);
      setCombinedData(demoData.combinedData || []);
      return;
    }
    const fetchYearData = async () => {
      try {
        const yearDataResponse = await getYearData();
        setYearData(yearDataResponse);

        // Combine reviewsData with yearData
        const combined = reviewsData.map((currentData) => {
          const historicalData = yearDataResponse.find(
            (yd) => yd.maps_uri === currentData.maps_uri
          );
          return {
            ...currentData,
            historicalData: historicalData?.data || null,
          };
        });
        setCombinedData(combined);
      } catch (error) {
        logger.error('Error fetching year data:', error);
      }
    };

    if (reviewsData.length > 0) {
      fetchYearData();
    }
  }, [reviewsData]);

  // Handle business click - use combinedData to get historicalData
  const handleBusinessClick = (business) => {
    // Find the business in combinedData to get the full data with historicalData
    const businessWithData = combinedData.find(
      (b) => b.maps_uri === business.maps_uri
    );
    setSelectedBusiness(businessWithData || business);
    setIsBusinessModalOpen(true);
  };

  const closeBusinessModal = () => {
    setIsBusinessModalOpen(false);
    setSelectedBusiness(null);
  };

  // Format number helper
  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  // Process traffic data from reviews
  const businessesWithTraffic = useMemo(() => {
    return reviewsData.filter(business => 
      business.traffic_data?.metrics?.estimated_monthly_visitors > 0
    ).sort((a, b) => 
      (b.traffic_data?.metrics?.estimated_monthly_visitors || 0) - 
      (a.traffic_data?.metrics?.estimated_monthly_visitors || 0)
    );
  }, [reviewsData]);

  // Calculate traffic statistics
  const trafficStats = useMemo(() => {
    if (!businessesWithTraffic.length) return null;
    
    const totalVisitors = businessesWithTraffic.reduce((sum, business) => 
      sum + (business.traffic_data?.metrics?.estimated_monthly_visitors || 0), 0
    );
    
    const avgVisitors = Math.round(totalVisitors / businessesWithTraffic.length);
    const maxVisitors = Math.max(...businessesWithTraffic.map(b => 
      b.traffic_data?.metrics?.estimated_monthly_visitors || 0
    ));
    
    // Calculate median
    const sortedVisitors = businessesWithTraffic
      .map(b => b.traffic_data?.metrics?.estimated_monthly_visitors || 0)
      .sort((a, b) => a - b);
    const mid = Math.floor(sortedVisitors.length / 2);
    const medianVisitors = sortedVisitors.length % 2 === 0
      ? (sortedVisitors[mid - 1] + sortedVisitors[mid]) / 2
      : sortedVisitors[mid];
    
    return {
      totalBusinesses: businessesWithTraffic.length,
      totalVisitors,
      avgVisitors,
      maxVisitors,
      medianVisitors,
    };
  }, [businessesWithTraffic]);

  // Top 3 for podium
  const top3Traffic = useMemo(() => {
    return businessesWithTraffic.slice(0, 3);
  }, [businessesWithTraffic]);

  // Market share calculation
  const marketShareData = useMemo(() => {
    if (!trafficStats || !businessesWithTraffic.length) return [];
    
    const top5 = businessesWithTraffic.slice(0, 5);
    const othersTraffic = businessesWithTraffic.slice(5).reduce((sum, b) => 
      sum + (b.traffic_data?.metrics?.estimated_monthly_visitors || 0), 0
    );
    
    const data = top5.map((business, index) => ({
      name: business.name.length > 15 ? business.name.substring(0, 15) + '...' : business.name,
      fullName: business.name,
      value: business.traffic_data?.metrics?.estimated_monthly_visitors || 0,
      percentage: ((business.traffic_data?.metrics?.estimated_monthly_visitors || 0) / trafficStats.totalVisitors * 100).toFixed(1),
      color: COLORS.chart[index],
      business: business
    }));
    
    if (othersTraffic > 0 && businessesWithTraffic.length > 5) {
      data.push({
        name: 'Others',
        fullName: `${businessesWithTraffic.length - 5} other businesses`,
        value: othersTraffic,
        percentage: (othersTraffic / trafficStats.totalVisitors * 100).toFixed(1),
        color: '#CBD5E0',
        business: null
      });
    }
    
    return data;
  }, [businessesWithTraffic, trafficStats]);

  // Traffic vs Reviews scatter data
  const scatterData = useMemo(() => {
    return businessesWithTraffic.map((business, index) => ({
      name: business.name.length > 20 ? business.name.substring(0, 20) + '...' : business.name,
      fullName: business.name,
      x: business.rating_count || 0,
      y: business.traffic_data?.metrics?.estimated_monthly_visitors || 0,
      rating: business.rating_avg || 0,
      fill: COLORS.chart[index % COLORS.chart.length],
      business
    }));
  }, [businessesWithTraffic]);

  // Distribution data for bar chart
  const distributionData = useMemo(() => {
    return businessesWithTraffic.slice(0, 12).map((business, index) => ({
      name: business.name.split(' ').slice(0, 2).join(' '),
      fullName: business.name,
      visitors: business.traffic_data?.metrics?.estimated_monthly_visitors || 0,
      reviews: business.rating_count || 0,
      fill: COLORS.chart[index % COLORS.chart.length],
      business
    }));
  }, [businessesWithTraffic]);

  // Sorted arrays for BusinessReviewModal (using combinedData with historicalData)
  const sortedByTotal = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData].sort(
      (a, b) => (b.rating_count || 0) - (a.rating_count || 0)
    );
  }, [combinedData]);

  const sortedBySentiment = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData].map((business) => {
      const sentimentBreakdown = business.historicalData?.sentimentBreakdown || {
        positive: 0,
        neutral: 0,
        negative: 0,
      };
      const total =
        sentimentBreakdown.positive +
        sentimentBreakdown.neutral +
        sentimentBreakdown.negative;
      const sentimentScore =
        total > 0 ? (sentimentBreakdown.positive / total) * 100 : 0;
      return { ...business, sentimentScore };
    }).sort((a, b) => b.sentimentScore - a.sentimentScore);
  }, [combinedData]);

  const sortedByGrowth = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData].map((business) => {
      const monthlyData = business.historicalData?.monthlyReviewCounts || {};
      const months = Object.keys(monthlyData).sort();
      const recentMonths = months.slice(-3);
      const totalGrowth = recentMonths.reduce(
        (sum, month) => sum + (monthlyData[month] || 0),
        0
      );
      return { ...business, totalGrowth };
    }).sort((a, b) => b.totalGrowth - a.totalGrowth);
  }, [combinedData]);

  const sortedByTraffic = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData]
      .map((business) => {
        const trafficValue = getTrafficValue(business) || 0;
        return { ...business, trafficValue };
      })
      .filter((business) => business.trafficValue > 0)
      .sort((a, b) => b.trafficValue - a.trafficValue);
  }, [combinedData]);

  // Filtered and sorted businesses for table
  const filteredBusinesses = useMemo(() => {
    let filtered = businessesWithTraffic;
    
    if (searchQuery) {
      filtered = filtered.filter(b => 
        b.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return [...filtered].sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'name':
          aVal = a.name;
          bVal = b.name;
          return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        case 'visitors':
          aVal = a.traffic_data?.metrics?.estimated_monthly_visitors || 0;
          bVal = b.traffic_data?.metrics?.estimated_monthly_visitors || 0;
          break;
        case 'reviews':
          aVal = a.rating_count || 0;
          bVal = b.rating_count || 0;
          break;
        case 'rating':
          aVal = a.rating_avg || 0;
          bVal = b.rating_avg || 0;
          break;
        default:
          aVal = a.traffic_data?.metrics?.estimated_monthly_visitors || 0;
          bVal = b.traffic_data?.metrics?.estimated_monthly_visitors || 0;
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [businessesWithTraffic, searchQuery, sortField, sortDirection]);

  // Get traffic tier badge
  const getTrafficTier = (visitors, maxVisitors) => {
    const ratio = visitors / maxVisitors;
    if (ratio >= 0.6) return { label: 'Elite', color: 'purple', icon: FaCrown };
    if (ratio >= 0.3) return { label: 'Strong', color: 'green', icon: FaRocket };
    if (ratio >= 0.1) return { label: 'Growing', color: 'blue', icon: FaChartLine };
    return { label: 'Emerging', color: 'gray', icon: FaLightbulb };
  };

  // Custom tooltip component
  const CustomBarTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box bg={glassBg} backdropFilter="blur(10px)" p={4} borderRadius="xl" boxShadow="lg" border="1px solid" borderColor={borderColor}>
          <Text fontWeight="bold" color={textColor} mb={2}>{data.fullName}</Text>
          <HStack spacing={4}>
            <VStack align="start" spacing={0}>
              <Text fontSize="xs" color={mutedColor}>Visitors</Text>
              <Text fontWeight="bold" color={COLORS.primary}>{formatNumber(data.visitors)}</Text>
            </VStack>
            <VStack align="start" spacing={0}>
              <Text fontSize="xs" color={mutedColor}>Reviews</Text>
              <Text fontWeight="bold" color={COLORS.success}>{formatNumber(data.reviews)}</Text>
            </VStack>
          </HStack>
        </Box>
      );
    }
    return null;
  };

  // Empty state
  if (!reviewsData || reviewsData.length === 0) {
    return (
      <Box>
        {/* Section Label */}
        {!demoMode && <SectionLabel description="Monitor website traffic trends for you and your competitors. Understand who's capturing attention online and identify traffic opportunities.">
          Traffic
        </SectionLabel>}

        <Flex direction="column" align="center" justify="center" minH="400px" p={8}>
          <Icon as={FaGlobe} boxSize={16} color="gray.300" mb={4} />
          <Heading size="md" color="gray.500" mb={2}>No Traffic Data Available</Heading>
          <Text color="gray.400" textAlign="center">
            Traffic analytics will appear here once data is available for your market.
          </Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box>
      {/* Traffic Champions Podium */}
      {top3Traffic.length >= 3 && (
        <Box
          display={{ base: 'none', md: 'block' }}
          bg="transparent"
          mb={8}
        >
          <Flex justify="center" align="flex-end" gap={6} w="full">
            {/* Silver - Second Place */}
            <Box
              bg={cardBg}
              borderRadius="2xl"
              textAlign="center"
              position="relative"
              cursor="pointer"
              onClick={() => top3Traffic[1] && handleBusinessClick(top3Traffic[1])}
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{ 
                transform: 'translateY(-8px)', 
                shadow: '2xl',
              }}
              overflow="hidden"
              flex="1"
              maxW="400px"
              boxShadow="xl"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <Box h="4px" bgGradient="linear(to-r, gray.300, gray.500, gray.300)" />
              <VStack p={6} spacing={4}>
                <Flex
                  position="absolute"
                  top={4}
                  left={4}
                  minW="36px"
                  h="36px"
                  px={2}
                  borderRadius="full"
                  bgGradient="linear(to-br, gray.300, gray.500)"
                  justify="center"
                  align="center"
                  shadow="md"
                >
                  <Text color="white" fontWeight="bold" fontSize="md" lineHeight="1">2</Text>
                </Flex>
                
                <Icon as={FaMedal} color="gray.400" boxSize={6} />
                
                <Box p="3px" borderRadius="full" bgGradient="linear(to-br, gray.300, gray.500)">
                  <Avatar 
                    name={top3Traffic[1]?.name} 
                    src={businessImages[top3Traffic[1]?.maps_uri] || ''} 
                    bg="gray.100" 
                    boxSize="70px"
                    borderWidth="3px"
                    borderColor={cardBg}
                  />
                </Box>
                
                <VStack spacing={1}>
                  <Tooltip label={top3Traffic[1]?.name} placement="top" hasArrow openDelay={300}>
                    <Heading size="sm" noOfLines={1} color="gray.700">{top3Traffic[1]?.name}</Heading>
                  </Tooltip>
                  <Text 
                    fontWeight="extrabold" 
                    fontSize="2xl" 
                    bgGradient="linear(to-r, gray.600, gray.800)"
                    bgClip="text"
                  >
                    {formatNumber(top3Traffic[1]?.traffic_data?.metrics?.estimated_monthly_visitors || 0)}
                  </Text>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium" textTransform="uppercase" letterSpacing="wider">
                    monthly visitors
                  </Text>
                </VStack>
                
                <Box w="full" px={2}>
                  <Box h="6px" bg="gray.100" borderRadius="full" overflow="hidden">
                    <Box
                      h="full"
                      w={`${((top3Traffic[1]?.traffic_data?.metrics?.estimated_monthly_visitors || 0) / (top3Traffic[0]?.traffic_data?.metrics?.estimated_monthly_visitors || 1)) * 100}%`}
                      bgGradient="linear(to-r, gray.400, gray.500)"
                      borderRadius="full"
                      transition="width 0.5s ease-out"
                    />
                  </Box>
                </Box>
              </VStack>
            </Box>

            {/* Gold - First Place */}
            <Box
              bg={cardBg}
              borderRadius="2xl"
              textAlign="center"
              position="relative"
              cursor="pointer"
              onClick={() => top3Traffic[0] && handleBusinessClick(top3Traffic[0])}
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{ 
                transform: 'translateY(-12px) scale(1.02)', 
                shadow: '0 25px 50px -12px rgba(251, 191, 36, 0.4)',
              }}
              overflow="hidden"
              flex="1.15"
              maxW="450px"
              boxShadow="0 20px 40px -12px rgba(251, 191, 36, 0.3)"
              zIndex={2}
              mb={4}
              borderWidth="1px"
              borderColor="yellow.200"
            >
              <Box
                h="6px"
                bgGradient="linear(to-r, yellow.300, yellow.400, orange.400, yellow.400, yellow.300)"
                backgroundSize="200% 100%"
                animation="shimmer 2s linear infinite"
                sx={{
                  '@keyframes shimmer': {
                    '0%': { backgroundPosition: '200% 0' },
                    '100%': { backgroundPosition: '-200% 0' },
                  },
                }}
              />
              <VStack p={8} spacing={4}>
                <Flex
                  position="absolute"
                  top={4}
                  left={4}
                  minW="40px"
                  h="40px"
                  px={2}
                  borderRadius="full"
                  bgGradient="linear(to-br, yellow.300, orange.400)"
                  justify="center"
                  align="center"
                  shadow="lg"
                >
                  <Text color="white" fontWeight="bold" fontSize="lg" lineHeight="1">1</Text>
                </Flex>
                
                <Icon as={FaTrophy} color="yellow.400" boxSize={10} filter="drop-shadow(0 2px 4px rgba(251, 191, 36, 0.5))" />
                
                <Box p="4px" borderRadius="full" bgGradient="linear(to-br, yellow.300, orange.400, yellow.500)" shadow="lg">
                  <Avatar 
                    name={top3Traffic[0]?.name} 
                    src={businessImages[top3Traffic[0]?.maps_uri] || ''} 
                    bg="yellow.50" 
                    boxSize="90px"
                    borderWidth="4px"
                    borderColor={cardBg}
                  />
                </Box>
                
                <VStack spacing={1}>
                  <Tooltip label={top3Traffic[0]?.name} placement="top" hasArrow openDelay={300}>
                    <Heading size="md" noOfLines={1} color="gray.800">{top3Traffic[0]?.name}</Heading>
                  </Tooltip>
                  <Text 
                    fontWeight="extrabold" 
                    fontSize="3xl" 
                    bgGradient="linear(to-r, blue.500, cyan.500)"
                    bgClip="text"
                  >
                    {formatNumber(top3Traffic[0]?.traffic_data?.metrics?.estimated_monthly_visitors || 0)}
                  </Text>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium" textTransform="uppercase" letterSpacing="wider">
                    monthly visitors
                  </Text>
                </VStack>
                
                <Box w="full" px={2}>
                  <Box h="8px" bg="yellow.50" borderRadius="full" overflow="hidden">
                    <Box h="full" w="100%" bgGradient="linear(to-r, yellow.400, orange.400)" borderRadius="full" />
                  </Box>
                </Box>
              </VStack>
            </Box>

            {/* Bronze - Third Place */}
            <Box
              bg={cardBg}
              borderRadius="2xl"
              textAlign="center"
              position="relative"
              cursor="pointer"
              onClick={() => top3Traffic[2] && handleBusinessClick(top3Traffic[2])}
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{ 
                transform: 'translateY(-8px)', 
                shadow: '2xl',
              }}
              overflow="hidden"
              flex="1"
              maxW="400px"
              boxShadow="xl"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <Box h="4px" bgGradient="linear(to-r, orange.200, orange.400, orange.200)" />
              <VStack p={6} spacing={4}>
                <Flex
                  position="absolute"
                  top={4}
                  left={4}
                  minW="36px"
                  h="36px"
                  px={2}
                  borderRadius="full"
                  bgGradient="linear(to-br, orange.300, orange.500)"
                  justify="center"
                  align="center"
                  shadow="md"
                >
                  <Text color="white" fontWeight="bold" fontSize="md" lineHeight="1">3</Text>
                </Flex>
                
                <Icon as={FaAward} color="orange.400" boxSize={6} />
                
                <Box p="3px" borderRadius="full" bgGradient="linear(to-br, orange.300, orange.500)">
                  <Avatar 
                    name={top3Traffic[2]?.name} 
                    src={businessImages[top3Traffic[2]?.maps_uri] || ''} 
                    bg="orange.50" 
                    boxSize="70px"
                    borderWidth="3px"
                    borderColor={cardBg}
                  />
                </Box>
                
                <VStack spacing={1}>
                  <Tooltip label={top3Traffic[2]?.name} placement="top" hasArrow openDelay={300}>
                    <Heading size="sm" noOfLines={1} color="gray.700">{top3Traffic[2]?.name}</Heading>
                  </Tooltip>
                  <Text 
                    fontWeight="extrabold" 
                    fontSize="2xl" 
                    bgGradient="linear(to-r, orange.500, orange.600)"
                    bgClip="text"
                  >
                    {formatNumber(top3Traffic[2]?.traffic_data?.metrics?.estimated_monthly_visitors || 0)}
                  </Text>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium" textTransform="uppercase" letterSpacing="wider">
                    monthly visitors
                  </Text>
                </VStack>
                
                <Box w="full" px={2}>
                  <Box h="6px" bg="orange.50" borderRadius="full" overflow="hidden">
                    <Box
                      h="full"
                      w={`${((top3Traffic[2]?.traffic_data?.metrics?.estimated_monthly_visitors || 0) / (top3Traffic[0]?.traffic_data?.metrics?.estimated_monthly_visitors || 1)) * 100}%`}
                      bgGradient="linear(to-r, orange.300, orange.500)"
                      borderRadius="full"
                      transition="width 0.5s ease-out"
                    />
                  </Box>
                </Box>
              </VStack>
            </Box>
          </Flex>
        </Box>
      )}

      {/* Section Label */}
      {!demoMode && <SectionLabel description="Monitor website traffic trends for you and your competitors. Understand who's capturing attention online and identify traffic opportunities.">
        Traffic
      </SectionLabel>}

      {/* Analytics Section */}
      <Tabs variant="soft-rounded" colorScheme="blue" mb={8}>
        <TabList mb={6} flexWrap="wrap" gap={2}>
          <Tab _selected={{ bg: 'blue.500', color: 'white' }}>
            <HStack spacing={2}>
              <Icon as={FaChartBar} />
              <Text>Distribution</Text>
            </HStack>
          </Tab>
          <Tab _selected={{ bg: 'blue.500', color: 'white' }}>
            <HStack spacing={2}>
              <Icon as={FaChartPie} />
              <Text>Market Share</Text>
            </HStack>
          </Tab>
          <Tab _selected={{ bg: 'blue.500', color: 'white' }}>
            <HStack spacing={2}>
              <Icon as={FaChartLine} />
              <Text>Traffic vs Reviews</Text>
            </HStack>
          </Tab>
        </TabList>

        <TabPanels>
          {/* Distribution Panel */}
          <TabPanel p={0}>
            <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6}>
              <HStack justify="space-between" mb={6}>
                <VStack align="start" spacing={1}>
                  <Heading size="md" color={textColor}>Traffic Distribution</Heading>
                  <Text fontSize="sm" color={subtitleColor}>Top 12 businesses by monthly visitors</Text>
                </VStack>
                <Badge colorScheme="blue" fontSize="sm" px={3} py={1}>
                  Avg: {formatNumber(trafficStats?.avgVisitors || 0)}/mo
                </Badge>
              </HStack>
              
              <Box h={{ base: "280px", md: "400px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={distributionData} margin={{ top: 10, right: 30, left: 10, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={borderColor} />
                    <XAxis 
                      dataKey="name" 
                      angle={-45} 
                      textAnchor="end" 
                      height={80}
                      tick={{ fill: subtitleColor, fontSize: 13, fontWeight: 600 }}
                    />
                    <YAxis 
                      tick={{ fill: subtitleColor, fontSize: 13, fontWeight: 600 }}
                      tickFormatter={formatNumber}
                    />
                    <RechartsTooltip content={<CustomBarTooltip />} />
                    <Bar 
                      dataKey="visitors" 
                      radius={[8, 8, 0, 0]}
                      cursor="pointer"
                      onClick={(data) => data && data.business && handleBusinessClick(data.business)}
                      label={FavoriteStarBarLabel}
                    >
                      {distributionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                    <ReferenceLine 
                      y={trafficStats?.avgVisitors || 0} 
                      stroke={COLORS.warning}
                      strokeDasharray="5 5"
                      strokeWidth={2}
                      label={{ 
                        value: 'Average', 
                        position: 'right',
                        fill: COLORS.warning,
                        fontSize: 12
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </TabPanel>

          {/* Market Share Panel */}
          <TabPanel p={0}>
            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
              <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6}>
                <VStack align="start" spacing={1} mb={6}>
                  <Heading size="md" color={textColor}>Market Share</Heading>
                  <Text fontSize="sm" color={subtitleColor}>Traffic distribution among top performers</Text>
                </VStack>
                
                <Box h="350px" overflow="visible">
                  <ResponsiveContainer width="100%" height="100%" style={{ overflow: 'visible' }}>
                    <PieChart style={{ overflow: 'visible' }}>
                      <Pie
                        data={marketShareData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={3}
                        dataKey="value"
                        label={FavoriteStarPieLabel}
                        labelLine={{ stroke: '#4A5568', strokeWidth: 1 }}
                        cursor="pointer"
                        onClick={(data) => data && data.business && handleBusinessClick(data.business)}
                      >
                        {marketShareData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <Box bg={glassBg} backdropFilter="blur(10px)" p={3} borderRadius="lg" boxShadow="md">
                                <Text fontWeight="bold" color={textColor}>{data.fullName}</Text>
                                <Text color={subtitleColor}>{formatNumber(data.value)} visitors</Text>
                                <Text color={data.color} fontWeight="bold">{data.percentage}% share</Text>
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
              
              {/* Market Share Legend */}
              <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6}>
                <VStack align="start" spacing={1} mb={6}>
                  <Heading size="md" color={textColor}>Market Breakdown</Heading>
                  <Text fontSize="sm" color={subtitleColor}>Click on a segment to view details</Text>
                </VStack>
                
                <VStack spacing={3} align="stretch">
                  {marketShareData.map((item, index) => (
                    <HStack 
                      key={index}
                      p={3}
                      bg={statBg}
                      borderRadius="lg"
                      cursor={item.business ? 'pointer' : 'default'}
                      onClick={() => item.business && handleBusinessClick(item.business)}
                      _hover={item.business ? { bg: hoverBg, transform: 'translateX(4px)' } : {}}
                      transition="all 0.2s"
                    >
                      <Box w={3} h={3} borderRadius="full" bg={item.color} />
                      <VStack align="start" spacing={0} flex={1}>
                        <Text fontWeight="medium" color={textColor} fontSize="sm">{item.fullName}</Text>
                        <Text fontSize="xs" color={subtitleColor}>{formatNumber(item.value)} visitors</Text>
                      </VStack>
                      <VStack align="end" spacing={0}>
                        <Text fontWeight="bold" color={textColor}>{item.percentage}%</Text>
                        <Progress 
                          value={parseFloat(item.percentage)} 
                          size="xs" 
                          w="60px" 
                          colorScheme={index === 0 ? 'purple' : 'blue'}
                          borderRadius="full"
                        />
                      </VStack>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            </Grid>
          </TabPanel>

          {/* Traffic vs Reviews Panel */}
          <TabPanel p={0}>
            <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6}>
              <VStack align="start" spacing={1} mb={6}>
                <Heading size="md" color={textColor}>Traffic vs Reviews Correlation</Heading>
                <Text fontSize="sm" color={subtitleColor}>
                  Explore the relationship between review count and web traffic
                </Text>
              </VStack>
              
              <Box h="450px">
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart margin={{ top: 20, right: 30, bottom: 60, left: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={borderColor} />
                    <XAxis 
                      type="number" 
                      dataKey="x" 
                      name="Reviews"
                      label={{ value: 'Total Reviews', position: 'bottom', offset: 40, fill: subtitleColor, fontWeight: 600 }}
                      tick={{ fill: subtitleColor, fontSize: 13, fontWeight: 600 }}
                    />
                    <YAxis 
                      type="number" 
                      dataKey="y" 
                      name="Visitors"
                      label={{ value: 'Monthly Visitors', angle: -90, position: 'left', offset: 40, fill: subtitleColor, fontWeight: 600 }}
                      tick={{ fill: subtitleColor, fontSize: 13, fontWeight: 600 }}
                      tickFormatter={formatNumber}
                    />
                    <RechartsTooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <Box bg={glassBg} backdropFilter="blur(10px)" p={4} borderRadius="lg" boxShadow="lg">
                              <Text fontWeight="bold" color={textColor} mb={2}>{data.fullName}</Text>
                              <SimpleGrid columns={2} spacing={3}>
                                <VStack align="start" spacing={0}>
                                  <Text fontSize="xs" color={mutedColor}>Reviews</Text>
                                  <Text fontWeight="bold" color={COLORS.success}>{formatNumber(data.x)}</Text>
                                </VStack>
                                <VStack align="start" spacing={0}>
                                  <Text fontSize="xs" color={mutedColor}>Visitors</Text>
                                  <Text fontWeight="bold" color={COLORS.primary}>{formatNumber(data.y)}</Text>
                                </VStack>
                                <VStack align="start" spacing={0}>
                                  <Text fontSize="xs" color={mutedColor}>Rating</Text>
                                  <HStack>
                                    <Icon as={FaStar} color="yellow.400" boxSize={3} />
                                    <Text fontWeight="bold">{data.rating.toFixed(1)}</Text>
                                  </HStack>
                                </VStack>
                              </SimpleGrid>
                            </Box>
                          );
                        }
                        return null;
                      }}
                    />
                    <Scatter 
                      data={scatterData} 
                      cursor="pointer"
                      onClick={(data) => data && data.business && handleBusinessClick(data.business)}
                    >
                      {scatterData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} fillOpacity={0.8} />
                      ))}
                    </Scatter>
                    <ReferenceLine 
                      y={trafficStats?.avgVisitors || 0} 
                      stroke={COLORS.warning}
                      strokeDasharray="5 5"
                      label={{ value: 'Avg Traffic', position: 'right', fill: COLORS.warning, fontSize: 13, fontWeight: 600 }}
                    />
                  </ScatterChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </TabPanel>

        </TabPanels>
      </Tabs>

      {/* Business Performance Table */}
      <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} overflow="hidden">
        <Box p={6} borderBottomWidth="1px" borderColor={borderColor}>
          <HStack justify="space-between" flexWrap="wrap" gap={4}>
            <VStack align="start" spacing={1}>
              <Heading size="md" color={textColor}>All Businesses</Heading>
              <Text fontSize="sm" color={subtitleColor}>
                {filteredBusinesses.length} businesses with traffic data
              </Text>
            </VStack>
            
            <HStack spacing={4} flexWrap="wrap">
              <InputGroup size="sm" maxW="250px">
                <InputLeftElement>
                  <Icon as={FaSearch} color="gray.400" />
                </InputLeftElement>
                <Input 
                  placeholder="Search businesses..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  borderRadius="lg"
                />
              </InputGroup>
              
              <Select 
                size="sm" 
                value={sortField} 
                onChange={(e) => setSortField(e.target.value)}
                maxW="150px"
                borderRadius="lg"
              >
                <option value="visitors">Traffic</option>
                <option value="reviews">Reviews</option>
                <option value="rating">Rating</option>
                <option value="name">Name</option>
              </Select>
              
              <IconButton
                size="sm"
                icon={<Icon as={sortDirection === 'asc' ? FaArrowUp : FaArrowDown} />}
                onClick={() => setSortDirection(d => d === 'asc' ? 'desc' : 'asc')}
                aria-label="Toggle sort direction"
                variant="outline"
                borderRadius="lg"
              />
            </HStack>
          </HStack>
        </Box>
        
        <TableContainer>
          <Table variant="simple">
            <Thead bg={statBg}>
              <Tr>
                <Th color={subtitleColor} fontSize="xs" py={4}>Business</Th>
                <Th color={subtitleColor} fontSize="xs" isNumeric>Monthly Visitors</Th>
                <Th color={subtitleColor} fontSize="xs" isNumeric>Reviews</Th>
                <Th color={subtitleColor} fontSize="xs" isNumeric>Rating</Th>
                <Th color={subtitleColor} fontSize="xs">Performance</Th>
                <Th color={subtitleColor} fontSize="xs">Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredBusinesses.slice(0, 20).map((business, index) => {
                const visitors = business.traffic_data?.metrics?.estimated_monthly_visitors || 0;
                const tier = getTrafficTier(visitors, trafficStats?.maxVisitors || 1);
                const marketShare = trafficStats ? (visitors / trafficStats.totalVisitors * 100).toFixed(1) : 0;
                const social = business.traffic_data?.metrics?.social || {};
                const socialCount = Object.values(social).filter(Boolean).length;
                
                return (
                  <Tr 
                    key={index}
                    cursor="pointer"
                    onClick={() => handleBusinessClick(business)}
                    _hover={{ bg: hoverBg }}
                    transition="all 0.2s"
                  >
                    <Td>
                      <HStack spacing={3}>
                        <Avatar 
                          size="sm" 
                          name={business.name} 
                          src={businessImages[business.maps_uri]}
                        />
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="medium" color={textColor} fontSize="sm">
                            {business.name}
                          </Text>
                          {business.primary_type && (
                            <Text fontSize="xs" color={subtitleColor}>
                              {business.primary_type.replace(/_/g, ' ')}
                            </Text>
                          )}
                        </VStack>
                      </HStack>
                    </Td>
                    <Td isNumeric>
                      <VStack align="end" spacing={0}>
                        <Text fontWeight="bold" color={tier.color + '.600'}>
                          {formatNumber(visitors)}
                        </Text>
                        <Text fontSize="xs" color={subtitleColor}>{marketShare}% share</Text>
                      </VStack>
                    </Td>
                    <Td isNumeric>
                      <Text color={textColor}>{formatNumber(business.rating_count || 0)}</Text>
                    </Td>
                    <Td isNumeric>
                      <HStack justify="flex-end" spacing={1}>
                        <Icon as={FaStar} color="yellow.400" boxSize={4} />
                        <Text fontWeight="medium" color={textColor}>
                          {business.rating_avg?.toFixed(1) || 'N/A'}
                        </Text>
                      </HStack>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Badge colorScheme={tier.color} variant="subtle" size="sm">
                          <HStack spacing={1}>
                            <Icon as={tier.icon} boxSize={3} />
                            <Text>{tier.label}</Text>
                          </HStack>
                        </Badge>
                        <Progress 
                          value={(visitors / (trafficStats?.maxVisitors || 1)) * 100} 
                          size="sm"
                          w="60px"
                          colorScheme={tier.color}
                          borderRadius="full"
                          bg={statBg}
                        />
                      </HStack>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Tooltip label={`${socialCount}/3 social profiles`} hasArrow>
                          <Badge colorScheme={socialCount >= 2 ? 'green' : socialCount === 1 ? 'yellow' : 'gray'} variant="subtle">
                            {socialCount}/3
                          </Badge>
                        </Tooltip>
                        {business.website_uri && (
                          <Tooltip label="Visit website" hasArrow>
                            <IconButton
                              size="xs"
                              icon={<Icon as={FaExternalLinkAlt} />}
                              variant="ghost"
                              colorScheme="blue"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(business.website_uri, '_blank');
                              }}
                              aria-label="Visit website"
                            />
                          </Tooltip>
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
        
        {filteredBusinesses.length > 20 && (
          <Box p={4} textAlign="center" borderTopWidth="1px" borderColor={borderColor}>
            <Text fontSize="sm" color={subtitleColor}>
              Showing 20 of {filteredBusinesses.length} businesses
            </Text>
          </Box>
        )}
      </Box>
      
      {/* Business Review Modal */}
      <BusinessReviewModal
        isOpen={isBusinessModalOpen}
        onClose={closeBusinessModal}
        business={selectedBusiness}
        yearData={yearData}
        sortedByTotal={sortedByTotal}
        sortedBySentiment={sortedBySentiment}
        sortedByGrowth={sortedByGrowth}
        sortedByTraffic={sortedByTraffic}
        demoMode={demoMode}
        demoTrafficHistory={demoMode ? demoData?.modalTrafficHistory : undefined}
        demoPricingData={demoMode ? demoData?.buildDemoPricing : undefined}
      />
    </Box>
  );
};

export default TrafficDashboard;
